import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp } from "lucide-react";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { useNavigation } from "@/providers/navigation/NavigationContext";
import { ClientViewingContext } from "@/shared/domain/agentThread/AgentThreadRecord";
import { useCollection } from "@/shared/domain/collections/hooks/useCollection";
import { useDataset } from "@/shared/domain/datasets/hooks/useDataset";
import { useFile } from "@/shared/domain/files/hooks/useFile";
import { sessionQueryKeys } from "@/shared/domain/sessions/hooks";
import { useView } from "@/shared/domain/views/hooks/useView";
import { pluralize, truncateMiddle } from "@/shared/text";

import { ContextIcon } from "../../AgentThreadInputBox/ContextIcon";
import {
  buildContextSummary,
  getVisualizerPanelCount,
  isEmptyContext,
} from "../../contextUtils";

import styles from "./ContextShiftIndicator.module.css";

interface ContextShiftIndicatorProps {
  context: ClientViewingContext;
}

/**
 * Displays a subtle inline indicator when thread context changed.
 * Shows a horizontal line with a toggle button; clicking expands
 * to reveal clickable dataset names, file names, and visualizer state.
 *
 * Empty contexts (user navigated away from everything) render as a
 * static, non-expandable label since there are no details to show.
 */
export function ContextShiftIndicator({ context }: ContextShiftIndicatorProps) {
  const [expanded, setExpanded] = React.useState(false);

  const isContextCleared = isEmptyContext(context);
  const summary = buildContextSummary(context);

  return (
    <div>
      <div className={styles.container}>
        <hr className={styles.line} />
        {isContextCleared ? (
          <span className={styles.label}>{summary}</span>
        ) : (
          <button
            className={styles.toggleButton}
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-label="Toggle context details"
          >
            {summary}
            {expanded ? (
              <ChevronUp className={styles.toggleIcon} />
            ) : (
              <ChevronDown className={styles.toggleIcon} />
            )}
          </button>
        )}
        <hr className={styles.line} />
      </div>

      {!isContextCleared && expanded && (
        <div className={styles.detailContainer}>
          {context.dataset_ids.map((id) => (
            <DatasetDetail key={`ds-${id}`} datasetId={id} />
          ))}
          {context.file_ids.map((id) => (
            <FileDetail key={`f-${id}`} fileId={id} />
          ))}
          {context.collection_ids.map((id) => (
            <CollectionDetail key={`cl-${id}`} collectionId={id} />
          ))}
          {context.device_ids.map((id) => (
            <DeviceDetail key={`dv-${id}`} deviceId={id} />
          ))}
          {context.session_ids.map((id) => (
            <SessionDetail key={`se-${id}`} sessionId={id} />
          ))}
          {context.table_state && (
            <TableDetail
              target={context.table_state.target}
              viewId={context.view_ids[0]}
            />
          )}
          {context.visualizer_state && (
            <ContextDetailItem
              icon={
                <ContextIcon
                  contextType="viz_state"
                  className={styles.contextIcon}
                />
              }
              label={vizLabel(context.visualizer_state)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components that resolve names via hooks
// ---------------------------------------------------------------------------

/** The View loaded on the table, named when its record has arrived, else the table itself. */
function tableLabel(
  target: string,
  viewId: string | undefined,
  viewName: string | undefined,
): string {
  if (viewId === undefined) {
    return `${target} table`;
  }
  return viewName ? `${viewName} (${viewId})` : viewId;
}

function TableDetail({
  target,
  viewId,
}: {
  target: string;
  viewId: string | undefined;
}) {
  const { data: view } = useView(viewId);

  const label = tableLabel(target, viewId, view?.name);

  return (
    <ContextDetailItem
      icon={<ContextIcon contextType="table" className={styles.contextIcon} />}
      label={label}
    />
  );
}

function DatasetDetail({ datasetId }: { datasetId: string }) {
  const { goto } = useNavigation();
  const { data: dataset } = useDataset(datasetId);

  const label = dataset?.name ? `${dataset.name} (${datasetId})` : datasetId;

  function handleClick() {
    goto.dataset({ datasetId });
  }

  return (
    <ContextDetailItem
      icon={
        <ContextIcon contextType="dataset" className={styles.contextIcon} />
      }
      label={label}
      onClick={handleClick}
    />
  );
}

function FileDetail({ fileId }: { fileId: string }) {
  const { goto } = useNavigation();
  const { data: file } = useFile(fileId);

  const displayName = file?.relative_path ?? fileId;
  const label = truncateMiddle(displayName, 40);
  const tooltip = displayName.length > 40 ? displayName : undefined;

  function handleClick() {
    goto.file({ fileId });
  }

  return (
    <ContextDetailItem
      icon={<ContextIcon contextType="file" className={styles.contextIcon} />}
      label={label}
      tooltip={tooltip}
      onClick={handleClick}
    />
  );
}

function CollectionDetail({ collectionId }: { collectionId: string }) {
  const { goto } = useNavigation();
  const { data: collection } = useCollection(collectionId);

  const label = collection?.name
    ? `${collection.name} (${collectionId})`
    : collectionId;

  function handleClick() {
    goto.collection({ collectionId });
  }

  return (
    <ContextDetailItem
      icon={
        <ContextIcon contextType="collection" className={styles.contextIcon} />
      }
      label={label}
      onClick={handleClick}
    />
  );
}

/**
 * Unlike the other details here, this resolves no record: a device's id is
 * user-chosen and is the only name it has, so there is nothing to look up.
 */
function DeviceDetail({ deviceId }: { deviceId: string }) {
  const { goto } = useNavigation();

  const label = truncateMiddle(deviceId, 40);
  const tooltip = deviceId.length > 40 ? deviceId : undefined;

  function handleClick() {
    goto.device({ deviceId });
  }

  return (
    <ContextDetailItem
      icon={<ContextIcon contextType="device" className={styles.contextIcon} />}
      label={label}
      tooltip={tooltip}
      onClick={handleClick}
    />
  );
}

/**
 * Not `useSession`: that suspends and throws to the nearest error boundary,
 * and a session deleted since the message was sent must not take the thread
 * down with it.
 */
function SessionDetail({ sessionId }: { sessionId: string }) {
  const { goto } = useNavigation();
  const { sessionService } = useDomainServices();
  const { data: session } = useQuery({
    queryKey: sessionQueryKeys.detail(sessionId),
    queryFn: ({ signal }) =>
      sessionService.getSessionById(sessionId, { abortSignal: signal }),
    staleTime: 60_000,
  });

  const label = session?.name ? `${session.name} (${sessionId})` : sessionId;

  function handleClick() {
    goto.session({ sessionId });
  }

  return (
    <ContextDetailItem
      icon={
        <ContextIcon contextType="session" className={styles.contextIcon} />
      }
      label={label}
      onClick={handleClick}
    />
  );
}

// ---------------------------------------------------------------------------
// Shared detail item
// ---------------------------------------------------------------------------

function ContextDetailItem({
  icon,
  label,
  tooltip,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  tooltip?: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      {icon}
      <span className={styles.contextLabel}>{label}</span>
    </>
  );

  if (onClick) {
    return (
      <button className={styles.detailButton} onClick={onClick} title={tooltip}>
        {content}
      </button>
    );
  }

  return (
    <span className={styles.detailItem} title={tooltip}>
      {content}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function vizLabel(state: Record<string, unknown>): string {
  const count = getVisualizerPanelCount(state);
  return count > 0 ? `${count} ${pluralize("panel", count)}` : "visualizer";
}
